"use client";

import type React from "react";
import { useState, useEffect } from "react";
import {
  VStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  IconButton,
  Flex,
  FormErrorMessage,
  Box,
} from "@chakra-ui/react";
import { Plus, X } from "lucide-react";
import type { PostCallAnalysisData } from "./PostCallAnalysis.tsx";
import { useAppTheme } from "../../../ThemeContext.tsx";

interface EnumAnalysisFormProps {
  onSave: (data: PostCallAnalysisData) => void;
  onCancel: () => void;
  initialData?: PostCallAnalysisData;
}

export const EnumAnalysisForm: React.FC<EnumAnalysisFormProps> = ({
  onSave,
  onCancel,
  initialData,
}) => {
  const [name, setName] = useState(initialData?.name || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [choices, setChoices] = useState<string[]>(initialData?.choices || []);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  useEffect(() => {
    if (initialData) {
      setName(initialData.name);
      setDescription(initialData.description);
      setChoices(initialData.choices || []);
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    if (choices.length === 0 || choices.some((choice) => choice.trim() === ""))
      return;

    onSave({
      type: "enum",
      name,
      description,
      choices: choices.filter((choice) => choice.trim() !== ""),
    });
  };

  const handleAddChoice = () => {
    setChoices([...choices, ""]);
  };

  const handleChoiceChange = (index: number, value: string) => {
    const newChoices = [...choices];
    newChoices[index] = value;
    setChoices(newChoices);
  };

  const handleRemoveChoice = (index: number) => {
    setChoices(choices.filter((_, i) => i !== index));
  };

  const isChoicesInvalid =
    isSubmitted &&
    (choices.length === 0 || choices.some((choice) => choice.trim() === ""));

  return (
    <Box mb={4}>
      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired>
            <FormLabel fontSize="sm">Name</FormLabel>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="customer_type"
              size="sm"
              _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
              textColor={isDark ? "white" : "gray.800"}
              bg={isDark ? "gray.700" : "white"}
              color={isDark ? "white" : "gray.800"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              variant="outline"
            />
          </FormControl>

          <FormControl isRequired>
            <FormLabel fontSize="sm">Description</FormLabel>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What type of customer is the user?"
              size="sm"
              _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
              textColor={isDark ? "white" : "gray.800"}
              bg={isDark ? "gray.700" : "white"}
              color={isDark ? "white" : "gray.800"}
              borderColor={isDark ? "gray.600" : "gray.300"}
              _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
              variant="outline"
            />
          </FormControl>

          <FormControl isRequired isInvalid={isChoicesInvalid}>
            <FormLabel fontSize="sm">Choices</FormLabel>
            <VStack spacing={2} align="stretch">
              {choices.map((choice, index) => (
                <Flex key={index} gap={2}>
                  <Input
                    value={choice}
                    onChange={(e) => handleChoiceChange(index, e.target.value)}
                    placeholder="1st_time_customer"
                    size="sm"
                    _placeholder={{ color: isDark ? "gray.400" : "gray.600" }}
                    textColor={isDark ? "white" : "gray.800"}
                    bg={isDark ? "gray.700" : "white"}
                    color={isDark ? "white" : "gray.800"}
                    borderColor={isDark ? "gray.600" : "gray.300"}
                    _hover={{ borderColor: isDark ? "gray.500" : "gray.400" }}
                    variant="outline"
                  />
                  <IconButton
                    aria-label="Remove choice"
                    icon={<X size={16} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemoveChoice(index)}
                    color={isDark ? "gray.400" : "gray.600"}
                    _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
                    isDisabled={choices.length === 1}
                  />
                </Flex>
              ))}
              <Button
                leftIcon={<Plus size={16} />}
                variant="ghost"
                size="sm"
                onClick={handleAddChoice}
                color={isDark ? "gray.400" : "gray.600"}
                _hover={{ bg: isDark ? "gray.700" : "gray.100" }}
                alignSelf="flex-start"
              >
                Add
              </Button>
            </VStack>
            {isChoicesInvalid && (
              <FormErrorMessage>
                At least one non-empty choice is required
              </FormErrorMessage>
            )}
          </FormControl>

          <Flex justify="flex-end" gap={2} pt={4}>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              colorScheme={isDark ? "black" : "white"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              colorScheme="gray"
              size="sm"
              _hover={{ bg: isDark ? "gray.600" : "gray.300" }}
              background={isDark ? "gray.700" : "gray.200"}
              textColor={isDark ? "whiteAlpha" : "gray.800"}
            >
              Save
            </Button>
          </Flex>
        </VStack>
      </form>
    </Box>
  );
};
